import { createConfig, http } from 'wagmi';
import { mainnet, polygon, arbitrum, optimism, bsc, avalanche, base, fantom, cronos, gnosis } from 'wagmi/chains';
import { metaMask, walletConnect, coinbaseWallet, injected } from 'wagmi/connectors';
// Top 10 EVM Networks by popularity and TVL
const defaultChains = [
    mainnet, // Ethereum (ETH) - Chain ID: 1
    polygon, // Polygon (MATIC) - Chain ID: 137  
    bsc, // Binance Smart Chain (BNB) - Chain ID: 56
    arbitrum, // Arbitrum One (ARB) - Chain ID: 42161
    optimism, // Optimism (OP) - Chain ID: 10
    avalanche, // Avalanche C-Chain (AVAX) - Chain ID: 43114
    base, // Base - Chain ID: 8453
    fantom, // Fantom (FTM) - Chain ID: 250
    cronos, // Cronos (CRO) - Chain ID: 25
    gnosis, // Gnosis Chain (xDAI) - Chain ID: 100
];
export function createCoinleyWalletConfig(options) {
    const chains = options.chains || defaultChains;
    const metadata = {
        name: options.appName,
        description: options.appDescription || `${options.appName} - Web3 Application`,
        url: options.appUrl || (typeof window !== 'undefined' ? window.location.origin : ''),
        icons: options.appIcon ? [options.appIcon] : []
    };
    const connectors = [
        // MetaMask connector - prefer this over generic injected for MetaMask
        metaMask(),
        // Generic injected connector for other wallets
        injected({
            shimDisconnect: true
        }),
        // Coinbase Wallet - configure to prefer extension over popup
        coinbaseWallet({
            appName: options.appName,
            appLogoUrl: options.appIcon,
            preference: 'smartWalletOnly' // This should use extension first
        })
    ];
    // Only add WalletConnect if projectId is provided
    if (options.projectId) {
        connectors.push(walletConnect({
            projectId: options.projectId,
            metadata,
            showQrModal: false // We'll handle the modal ourselves
        }));
    }
    // Enhanced RPC transports with fallbacks for better reliability
    const createTransport = (chain) => {
        // Use multiple RPC endpoints for better reliability
        const rpcUrls = {
            1: ['https://eth.merkle.io', 'https://rpc.ankr.com/eth'],
            137: ['https://polygon-rpc.com', 'https://rpc.ankr.com/polygon'],
            56: ['https://bsc-dataseed.binance.org', 'https://rpc.ankr.com/bsc'],
            42161: ['https://arb1.arbitrum.io/rpc', 'https://rpc.ankr.com/arbitrum'],
            10: ['https://mainnet.optimism.io', 'https://rpc.ankr.com/optimism'],
            43114: ['https://api.avax.network/ext/bc/C/rpc', 'https://rpc.ankr.com/avalanche'],
            8453: ['https://base.publicnode.com', 'https://base.llamarpc.com'], // Better Base RPCs
            250: ['https://rpc.ftm.tools', 'https://rpc.ankr.com/fantom'],
            25: ['https://evm.cronos.org', 'https://cronos.blockpi.network/v1/rpc/public'],
            100: ['https://rpc.gnosischain.com', 'https://gnosis.publicnode.com']
        };
        const urls = rpcUrls[chain.id] || [chain.rpcUrls?.default?.http?.[0]];
        return http(urls[0]); // Use primary RPC
    };
    return createConfig({
        chains: chains,
        connectors,
        transports: chains.reduce((acc, chain) => ({
            ...acc,
            [chain.id]: createTransport(chain)
        }), {}),
        ssr: true
    });
}
// Top 10 EVM chain configurations for easy access
export const popularChains = {
    ethereum: mainnet, // Chain ID: 1 - The original EVM network
    polygon: polygon, // Chain ID: 137 - Layer 2 scaling solution
    bsc: bsc, // Chain ID: 56 - High throughput, lower fees
    arbitrum: arbitrum, // Chain ID: 42161 - Optimistic rollup Layer 2
    optimism: optimism, // Chain ID: 10 - Another major Optimistic rollup
    avalanche: avalanche, // Chain ID: 43114 - Fast finality and low fees
    base: base, // Chain ID: 8453 - Coinbase's Layer 2 solution
    fantom: fantom, // Chain ID: 250 - Fast and cheap transactions
    cronos: cronos, // Chain ID: 25 - Crypto.com's blockchain
    gnosis: gnosis, // Chain ID: 100 - Stable transaction costs
};
// Helper to create config with popular chains
export function createQuickConfig(options) {
    const chains = options.chains
        ? options.chains.map(name => popularChains[name])
        : defaultChains;
    return createCoinleyWalletConfig({
        ...options,
        chains
    });
}
//# sourceMappingURL=config.js.map